import { Navigate, useLocation } from "react-router-dom";
import { UserType } from "@/types/user";
import { useAuth } from "@/hooks/useAuth";
import NotFound from "@/pages/NotFound";
import { buildRedirectAwarePath } from "@/utils/redirectUtils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import NativeAppSkeleton from "@/components/native/NativeAppSkeleton";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedUserTypes: UserType[];
}

export const ProtectedRoute = ({
  children,
  allowedUserTypes,
}: ProtectedRouteProps) => {
  const { user, isLoading, isNativeSessionPending } = useAuth();
  const { isNative } = useNativePlatform();
  const location = useLocation();
  const requestedPath = `${location.pathname}${location.search}${location.hash}`;

  if (isLoading || (isNative && isNativeSessionPending && !user)) {
    if (isNative) {
      return <NativeAppSkeleton />;
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={buildRedirectAwarePath("/auth", requestedPath)} replace />;
  }

  if (!allowedUserTypes.includes(user.type)) {
    return <NotFound />;
  }

  // Check if terms are accepted - if not, redirect to terms acceptance page
  // Admins should not need to accept terms
  if (user.accountTermsAccepted === false && user.type !== UserType.Admin) {
    return (
      <Navigate
        to={buildRedirectAwarePath("/accept-terms", requestedPath)}
        replace
      />
    );
  }

  return <>{children}</>;
};
